import { useSetAtom } from "jotai";

import { useEffect, useRef, useState } from "react";

import { useTranslation } from "react-i18next";

import { useNavigate, useSearchParams } from "react-router-dom";

import { googleLogin, setupGoogleLogin } from "../api/authApi";

import { authStatusAtom, authUserAtom } from "../atoms/authAtoms";

const GoogleCallbackPage = () => {
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();

  const { t } = useTranslation("common");

  const setAuthUser = useSetAtom(authUserAtom);

  const setAuthStatus = useSetAtom(authStatusAtom);

  const [error, setError] = useState<string | null>(null);

  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;

    processed.current = true;

    const code = searchParams.get("code");

    const state = searchParams.get("state");

    if (!code) {
      setError(t("LOGIN.googleAuthFailed", "Google authentication failed."));

      return;
    }

    const loginFn = state === "setup" ? setupGoogleLogin : googleLogin;

    loginFn(code)

      .then((session) => {
        // Backend set the admin session + CSRF cookies on this response.

        setAuthUser(session.user);

        setAuthStatus("authenticated");

        const dest = session.user.twoFactorEnabled
          ? "/integrations"
          : "/recommend-2fa";

        navigate(dest, { replace: true });
      })

      .catch(() => {
        setError(
          t(
            "LOGIN.googleAuthFailed",

            "Google authentication failed. Make sure your Google account is linked to an admin account.",
          ),
        );
      });
  }, [navigate, searchParams, setAuthStatus, setAuthUser, t]);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="flex flex-col items-center gap-4 text-center">
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 text-destructive px-4 py-3 text-sm">
            {error}
          </div>
          <a href="/login" className="text-sm text-primary hover:underline">
            {t("LOGIN.backToLogin", "Back to login")}
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <svg
          className="h-8 w-8 animate-spin text-primary"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
        <p className="text-sm text-muted-foreground">
          {t("LOGIN.signingInWithGoogle", "Signing in with Google...")}
        </p>
      </div>
    </div>
  );
};

export default GoogleCallbackPage;
